import { Injectable } from '@angular/core';
import { environment as ENV } from '../../environments/environment';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';

@Injectable()
export class AnalyticsService {
  constructor(private http: HttpClient) { }

  private checkDaysBehind(days?: number): boolean {
    return !!days;
  }

  private paramsYearDaysBehind(params: HttpParams): HttpParams {
    let ret = new HttpParams();
    const currentYear = String(new Date().getFullYear());
    const wholeYear = '9999';
    if (params.has('year') && params.has('daysBehind')) {
      const pYear = params.get('year');
      const pDaysBehind = params.get('daysBehind');
      if (pYear === currentYear && pDaysBehind === wholeYear) {
        ret = params.delete('daysBehind');
      } else if (pYear === currentYear && pDaysBehind !== wholeYear) {
        ret = params.delete('year');
      } else {
        ret = params.delete('daysBehind');
      }
    } else if (!params.has('year') && params.has('daysBehind') && params.get('daysBehind') === wholeYear) {
      params = params.delete('daysBehind');
      ret = params.delete('year');
      // ret = params.set('year', currentYear);
    } else {
      ret = params;
    }
    return ret;
  }

  // DASHBOARD
  getDashBoard(interval?: number, year: number = null): Observable<any> {
    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/dashboard`;

    let params = new HttpParams();

    if (interval) {
      params = params.set('interval', String(interval));
    }

    if (year) {
      params = params.set('year', String(year));
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    return this.http.get<any[]>(url);
  }

  // GRAPHS
  getJobsGraph(
    interval?: number,
    jobs_id?: number,
    daysBehind: number = null,
    year: number = null
  ): Observable<any> {
    const additionalQuery = jobs_id ? '/' + jobs_id : '';

    let params = new HttpParams();

    if (interval) {
      params = params.set('interval', String(interval));
    }

    if (this.checkDaysBehind(daysBehind)) {
      params = params.set('daysBehind', String(daysBehind));
    }

    if (year) {
      params = params.set('year', String(year));
    }

    params = this.paramsYearDaysBehind(params);

    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/jobs-graph${additionalQuery}`;

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }
    return this.http.get<any[]>(url);
  }

  getWebinarGraph(
    interval?: number,
    id?: number,
    daysBehind: number = null,
    year: number = null
  ): Observable<any> {
    const additionalQuery = id ? '/' + id : '';

    let params = new HttpParams();

    if (interval) {
      params = params.set('interval', String(interval));
    }

    if (this.checkDaysBehind(daysBehind)) {
      params = params.set('daysBehind', String(daysBehind));
    }

    if (year) {
      params = params.set('year', String(year));
    }

    params = this.paramsYearDaysBehind(params);
    // https://api-stage.tutored.me/api/v1/employer/analytics/webinars-graph/3263
    // let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/webinars/${id}/analytics`;
    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/webinars-graph/${id}`;

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }
    return this.http.get<any[]>(url);
  }

  getEventsGraph(
    events_id?: number,
    daysBehind: number = null,
    year: number = null
  ): Observable<any> {
    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/events-graph/${events_id}`;

    let params = new HttpParams();

    if (this.checkDaysBehind(daysBehind)) {
      params = params.set('daysBehind', String(daysBehind));
    }

    if (year) {
      params = params.set('year', String(year));
    }

    params = this.paramsYearDaysBehind(params);

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }

    return this.http.get<any[]>(url);
  }

  getPostsGraph(
    interval?: number,
    posts_id?: number,
    daysBehind: number = null,
    year: number = null
  ): Observable<any> {
    const additionalQuery = posts_id ? '/' + posts_id : '';

    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/posts-graph${additionalQuery}`;

    let params = new HttpParams();

    if (interval) {
      params = params.set('interval', String(interval));
    }

    if (this.checkDaysBehind(daysBehind)) {
      params = params.set('daysBehind', String(daysBehind));
    }

    if (year) {
      params = params.set('year', String(year));
    }

    params = this.paramsYearDaysBehind(params);

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }

    return this.http.get<any[]>(url);
  }

  getPollsGraph(
    interval?: number,
    polls_id?: number,
    daysBehind: number = null,
    year: number = null
  ): Observable<any> {
    const additionalQuery = polls_id ? '/' + polls_id : '';

    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/polls-graph${additionalQuery}`;

    let params = new HttpParams();

    if (interval) {
      params = params.set('interval', String(interval));
    }

    if (this.checkDaysBehind(daysBehind)) {
      params = params.set('daysBehind', String(daysBehind));
    }

    if (year) {
      params = params.set('year', String(year));
    }

    params = this.paramsYearDaysBehind(params);

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }
    return this.http.get<any[]>(url);
  }

  getTotalViewsGraph(
    interval?: number,
    daysBehind: number = null,
    year: number = null
  ): Observable<any> {
    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/total-views-graph`;

    let params = new HttpParams();

    if (interval) {
      params = params.set('interval', String(interval));
    }

    if (this.checkDaysBehind(daysBehind)) {
      params = params.set('daysBehind', String(daysBehind));
    }

    if (year) {
      params = params.set('year', String(year));
    }

    params = this.paramsYearDaysBehind(params);

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }
    return this.http.get<any[]>(url);
  }

  // TOP
  getSingleJobTop(jobs_id: number, year: number = null): Observable<any> {
    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/top/single-job/${jobs_id}`;

    let params = new HttpParams();

    if (year) {
      params = params.set('year', String(year));
    }

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }

    return this.http.get<any[]>(url);
  }

  getSingleWebinarTop(id: number, year: number = null): Observable<any> {
    // https://api-stage.tutored.me/api/v1/employer/analytics/top/single-webinar/3263
    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/top/single-webinar/${id}`;

    let params = new HttpParams();

    if (year) {
      params = params.set('year', String(year));
    }

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }

    return this.http.get<any[]>(url);
  }

  getSingleEventTop(event_id: number, year: number = null): Observable<any> {
    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/top/single-event/${event_id}`;

    let params = new HttpParams();

    if (year) {
      params = params.set('year', String(year));
    }

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }

    return this.http.get<any[]>(url);
  }

  getSinglePostTop(post_id: number, year: number = null): Observable<any> {
    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/top/single-post/${post_id}`;

    let params = new HttpParams();

    if (year) {
      params = params.set('year', String(year));
    }

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }

    return this.http.get<any[]>(url);
  }

  getSinglePollTop(
    poll_id: number,
    answers_id?: number,
    year: number = null
  ): Observable<any> {
    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/top/single-poll/${poll_id}`;

    let params = new HttpParams();

    if (answers_id) {
      url += `/${answers_id}`;
    }

    if (year) {
      params = params.set('year', String(year));
    }

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }
    return this.http.get<any[]>(url);
  }

  getSinglePollTopAnswers(campaigns_id: number): Observable<any> {
    const url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/single-poll-answers/${campaigns_id}`;

    return this.http.get<any[]>(url);
  }

  getSinglePollAnswers(poll_id: number, year: number = null): Observable<any> {
    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/single-poll-answers/${poll_id}`;

    let params = new HttpParams();

    if (year) {
      params = params.set('year', String(year));
    }

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }

    return this.http.get<any[]>(url);
  }

  getPollsTop(year: number = null): Observable<any> {
    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/top/polls`;

    let params = new HttpParams();

    if (year) {
      params = params.set('year', String(year));
    }

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }

    return this.http.get<any[]>(url);
  }

  getPostsTop(year: number = null): Observable<any> {
    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/top/posts`;

    let params = new HttpParams();

    if (year) {
      params = params.set('year', String(year));
    }

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }

    return this.http.get<any[]>(url);
  }

  getJobsTop(year: number = null): Observable<any> {
    let url = `${ENV.baseUrlWs}${ENV.wsVerison}employer/analytics/top/jobs`;

    let params = new HttpParams();

    if (year) {
      params = params.set('year', String(year));
    }

    if (params.toString()) {
      url += `/?${params.toString()}`;
    }

    return this.http.get<any[]>(url);
  }
}
